import type {
  EmissionsByScopeAndMonthResponse,
  MonthAxis,
  MonthValue,
} from "../types/dashboard";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function monthKey(label: unknown): string {
  const text = String(label ?? "").trim();
  if (/^\d{4}-(0[1-9]|1[0-2])$/.test(text)) return text;
  const month = text.match(
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i,
  );
  const year = text.match(/(?:19|20)\d{2}/);
  if (!month || !year) return "";
  const index = MONTHS.findIndex(
    (name) => name.toLowerCase() === month[1].toLowerCase(),
  );
  return `${year[0]}-${String(index + 1).padStart(2, "0")}`;
}

export function monthAxis(labels: unknown[]): MonthAxis[] {
  return [...new Set(labels.map(monthKey).filter(Boolean))]
    .sort()
    .map((key) => {
      const [year, number] = key.split("-");
      const month = MONTHS[Number(number) - 1];
      return {
        year,
        from_month: Number(number),
        month,
        months: `${month} - ${year}`,
      };
    });
}

export function alignMonths(
  points: MonthValue[],
  axis: MonthAxis[],
): MonthValue[] {
  const values = new Map<string, number>();
  for (const point of points) {
    const key = monthKey(point.name);
    values.set(key, (values.get(key) ?? 0) + point.value);
  }
  return axis.map((x) => ({
    name: x.months,
    value: values.get(monthKey(x.months)) ?? 0,
  }));
}

/** Reactor Tuple3 serializes as t1/t2/t3. Monthly live charts use its scope level,
 * avoiding double counting the same emissions in the category and subcategory levels. */
export function normalizeMonthlyEmissions(
  raw: unknown,
): EmissionsByScopeAndMonthResponse {
  const tuple = raw as { t1?: unknown; t2?: unknown; t3?: unknown } | null;
  if (
    !tuple ||
    !Array.isArray(tuple.t1) ||
    !Array.isArray(tuple.t2) ||
    !Array.isArray(tuple.t3)
  ) {
    throw new Error("Invalid monthly emissions response");
  }
  const data = tuple.t1.map((row: unknown) => {
    const scope = row as {
      id: number;
      code: string;
      data: Record<string, unknown>;
    } | null;
    if (
      !scope ||
      !Number.isFinite(scope.id) ||
      typeof scope.code !== "string" ||
      !scope.data ||
      typeof scope.data !== "object" ||
      Array.isArray(scope.data)
    ) {
      throw new Error("Invalid monthly emissions series");
    }
    const points = Object.entries(scope.data).map(([name, value]) => {
      if (
        !monthKey(name) ||
        typeof value !== "number" ||
        !Number.isFinite(value)
      ) {
        throw new Error("Invalid monthly emissions point");
      }
      return { name, value };
    });
    return { id: scope.id, scope: scope.id, name: scope.code, data: points };
  });
  const xaxis = monthAxis(
    data.flatMap((row) => row.data.map((point) => point.name)),
  );
  return {
    xaxis,
    data: data.map((row) => ({ ...row, data: alignMonths(row.data, xaxis) })),
  };
}
